// src/pages/modals/FinalDecisionModal.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";

export default function FinalDecisionModal({
  selectedApplication,
  finalDoc,
  onClose,
  comment,
  setComment,
  onApprove,
  onReject,
  locale = "kz",
}) {
  // ---- hooks must be at the top (no early returns) ----
  const lang = locale === "ru" ? "ru" : "kz";
  const tt = useMemo(
    () =>
      ({
        kz: {
          titlePrefix: "Өтініш:",
          close: "Жабу",
          comment: "Түсіндірме (міндетті):",
          approve: "Қабылдау",
          reject: "Қайтару",
          file: "Файл:",
          download: "Жүктеу",
          average: "Орташа балл:",
          verdict: "Қорытынды:",
        },
        ru: {
          titlePrefix: "Заявка:",
          close: "Закрыть",
          comment: "Комментарий (обязательно):",
          approve: "Принять",
          reject: "Отклонить",
          file: "Файл:",
          download: "Скачать",
          average: "Средний балл:",
          verdict: "Решение:",
        },
      })[lang],
    [lang]
  );

  const [pending, setPending] = useState(false);
  const taRef = useRef(null);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  useEffect(() => {
    if (selectedApplication && finalDoc) {
      taRef.current?.focus();
    }
  }, [selectedApplication, finalDoc]);

  const isOpen = Boolean(selectedApplication && finalDoc);
  const fileName =
    finalDoc?.file_path?.split("/").pop() ||
    finalDoc?.file_url?.split("/").pop() ||
    "";

  const handleApprove = async () => {
    if (pending) return;
    try {
      setPending(true);
      await onApprove?.();
    } finally {
      setPending(false);
    }
  };

  const handleReject = async () => {
    if (pending) return;
    try {
      setPending(true);
      await onReject?.();
    } finally {
      setPending(false);
    }
  };

  const onBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  // ---- conditional render (not early return with hooks above) ----
  if (!isOpen) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="fdm-title"
      onClick={onBackdropClick}
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0,0,0,.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        animation: "fdmFadeIn .2s ease",
      }}
    >
      <div
        style={{
          background: "#fff",
          width: "min(640px, 92vw)",
          borderRadius: 12,
          boxShadow: "0 12px 28px rgba(0,0,0,.18)",
          padding: 20,
          transform: "translateY(4px)",
          animation: "fdmPop .2s ease forwards",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* header */}
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <h3 id="fdm-title" style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>
            {tt.titlePrefix} {selectedApplication.title}
          </h3>
          <button
            onClick={onClose}
            title={tt.close}
            aria-label={tt.close}
            style={{
              marginLeft: "auto",
              border: "none",
              background: "transparent",
              fontSize: 22,
              lineHeight: 1,
              cursor: "pointer",
              padding: 6,
            }}
          >
            ×
          </button>
        </div>

        {/* meta */}
        <div style={{ marginTop: 12, display: "grid", gap: 6 }}>
          {fileName ? (
            <div>
              <strong>{tt.file}</strong>{" "}
              {finalDoc.file_url ? (
                <a href={finalDoc.file_url} target="_blank" rel="noopener noreferrer">
                  {tt.download} ({fileName})
                </a>
              ) : (
                <span>{fileName}</span>
              )}
            </div>
          ) : null}
          {"final_average" in finalDoc && (
            <div>
              <strong>{tt.average}</strong> {finalDoc.final_average ?? "—"}
            </div>
          )}
          {"summary_decision" in finalDoc && (
            <div>
              <strong>{tt.verdict}</strong> {finalDoc.summary_decision ?? "—"}
            </div>
          )}
        </div>

        {/* comment */}
        <label htmlFor="fdm-comment" style={{ display: "block", marginTop: 16, fontWeight: 600 }}>
          {tt.comment}
        </label>
        <textarea
          id="fdm-comment"
          ref={taRef}
          rows={3}
          value={comment}
          onChange={(e) => setComment?.(e.target.value)}
          placeholder={tt.comment}
          style={{
            width: "100%",
            marginTop: 6,
            borderRadius: 8,
            border: "1px solid #d1d5db",
            padding: 10,
            resize: "vertical",
            outline: "none",
          }}
        />

        {/* actions */}
        <div style={{ display: "flex", gap: 12, marginTop: 18 }}>
          <button
            onClick={handleApprove}
            disabled={pending}
            style={{
              flex: 1,
              background: "#2e7d32",
              color: "#fff",
              padding: "10px 12px",
              fontWeight: 700,
              borderRadius: 8,
              border: "none",
              cursor: pending ? "not-allowed" : "pointer",
              opacity: pending ? 0.7 : 1,
            }}
          >
            {tt.approve}
          </button>
          <button
            onClick={handleReject}
            disabled={pending}
            style={{
              flex: 1,
              background: "#c62828",
              color: "#fff",
              padding: "10px 12px",
              fontWeight: 700,
              borderRadius: 8,
              border: "none",
              cursor: pending ? "not-allowed" : "pointer",
              opacity: pending ? 0.7 : 1,
            }}
          >
            {tt.reject}
          </button>
        </div>
      </div>

      {/* animations */}
      <style>{`
        @keyframes fdmFadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes fdmPop { from { opacity: .96; transform: translateY(4px) } to { opacity: 1; transform: translateY(0) } }
      `}</style>
    </div>
  );
}
